import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/layout/PublicNav";
import { useAuth } from "@/context/AuthContext";

const SIDE = "https://images.unsplash.com/photo-1776875097847-49bd9bcf1eca?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

function GoogleButton({ label }) {
  const handle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" onClick={handle} data-testid="google-auth-btn"
      className="h-11 w-full rounded-md border-slate-300 bg-white font-semibold text-navy hover:bg-slate-50">
      <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
      {label}
    </Button>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) { toast.success("Welcome back!"); navigate(res.user.role === "admin" ? "/admin" : (res.user.activated ? "/dashboard" : "/onboarding")); }
    else setError(res.error);
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <Logo />
          <h1 className="mt-10 font-heading text-3xl font-bold tracking-tight text-navy">Welcome back</h1>
          <p className="mt-2 text-sm text-slate-500">Log in to access your resources and community.</p>
          <div className="mt-8"><GoogleButton label="Continue with Google" /></div>
          <div className="my-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
            <div className="h-px flex-1 bg-slate-200" /> or <div className="h-px flex-1 bg-slate-200" />
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="mt-1.5 h-11" placeholder="you@business.in" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="mt-1.5 h-11" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm font-medium text-red-600" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit" className="h-11 w-full rounded-md bg-navy font-semibold text-white hover:bg-navysurface">
              {loading ? "Logging in…" : "Log in"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-slate-500">New here? <Link to="/register" className="font-semibold text-emerald hover:underline" data-testid="login-to-register">Create a free account</Link></p>
        </div>
      </div>
      <div className="relative hidden bg-navy lg:block">
        <img src={SIDE} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-navy/80 to-navysurface/60" />
        <div className="relative flex h-full flex-col justify-end p-16">
          <p className="font-heading text-3xl font-bold leading-tight text-white" data-testid="login-side-line">Practical resources for Indian business owners.</p>
        </div>
      </div>
    </div>
  );
}

export { GoogleButton };
